import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { servicesContent } from '../data/servicesContent';

const scrollToContact = (service) => {
  if (service) window.dispatchEvent(new CustomEvent('soulboard:apply', { detail: { service } }));
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
};

const MethodLinkText = ({ text }) => {
  const [before, after] = text.split('Soulboard Method');
  if (after === undefined) return text;
  return (
    <>
      {before}
      <button
        type="button"
        onClick={() => document.getElementById('soulboard-method')?.scrollIntoView({ behavior: 'smooth' })}
        className="text-neutral-900 underline decoration-[#CCFF00] decoration-2 underline-offset-4 transition-colors duration-300 hover:text-[#7d65b8]"
        style={{ fontWeight: 600 }}
        data-testid="complete-journey-method-link"
      >
        Soulboard Method
      </button>
      {after}
    </>
  );
};

/**
 * COMPLETE SOULBOARD JOURNEY — compact, text led editorial closing block for
 * the Discover & Develop chapter. Cream background, centred, handwritten logo
 * as a decorative signature. No dark full screen block, no discount language,
 * no 01 + 02 = 03.
 */
const CompleteJourneySection = () => {
  const { language } = useLanguage();
  const c = servicesContent[language];

  return (
    <section
      id="complete-journey"
      className="bg-[#faf7ee] py-20 lg:py-24 overflow-hidden"
      data-testid="complete-journey-section"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          className="max-w-3xl mx-auto text-center space-y-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img
            src="/soulboard-logo.png"
            alt="Soulboard"
            className="w-40 lg:w-52 mx-auto"
            style={{ mixBlendMode: 'multiply' }}
            data-testid="journey-s-mark"
          />
          <p className="text-[11px] tracking-[0.5em] uppercase text-neutral-500">{c.cjEyebrow}</p>
          <h2
            className="offering-title leading-[0.98] text-neutral-900"
            style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}
            data-testid="complete-journey-title"
          >
            {c.cjTitle}
          </h2>
          <p className="text-base lg:text-lg italic text-neutral-700">{c.cjTagline}</p>
          <div className="mx-auto w-12 h-[2px] bg-[#CCFF00]" />

          <div className="space-y-3">
            {c.cjCopy.map((p, i) => (
              <p
                key={i}
                className={`${i === 0 ? 'text-base lg:text-lg text-neutral-800' : 'text-sm lg:text-base text-neutral-700'} leading-relaxed`}
              >
                {i === 0 ? <MethodLinkText text={p} /> : p}
              </p>
            ))}
          </div>

          <ul className="relative left-1/2 -translate-x-1/2 w-[min(72rem,calc(100vw-3rem))] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-6 border-y border-neutral-300 py-6 text-center" data-testid="complete-journey-timeline">
            {c.cjTimeline.map((s) => (
              <li key={s.step} className="px-3" data-testid={`timeline-${s.step.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-sm lg:text-base text-neutral-900" style={{ fontWeight: 600 }}>{s.step}</p>
                <p className="text-[10px] tracking-[0.35em] uppercase mt-1" style={{ color: '#7d65b8' }}>{s.time}</p>
              </li>
            ))}
          </ul>

          <p className="text-[10px] tracking-[0.35em] uppercase pt-1" style={{ color: '#7d65b8' }} data-testid="complete-journey-format">
            {c.cjFormat}
          </p>
          <p
            className="text-3xl lg:text-4xl text-neutral-900"
            style={{ fontWeight: 500 }}
            data-testid="complete-journey-price"
          >
            {c.cjPrice}
          </p>

          <div className="pt-2">
            <motion.button
              onClick={() => scrollToContact('complete-journey')}
              className="px-9 py-4 text-sm uppercase tracking-[0.2em] font-bold"
              style={{ background: '#CCFF00', color: '#171717' }}
              whileHover={{ scale: 1.03, boxShadow: '0 10px 24px rgba(204,255,0,0.25)' }}
              whileTap={{ scale: 0.97 }}
              data-testid="complete-journey-cta"
            >
              [ {c.cjCta} ]
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompleteJourneySection;
